import { useEffect, useState } from 'react';
import { crmFetch, dataOf } from '../chat/api';
import { useAuth } from '../context/AuthContext';

/* Whether this login has the Payouts page: at least one Account where it holds
   Statements at Full (CONTEXT.md "Payouts page"). Asked once per login, like
   useStatsAccess (a super admin has every enabled Account; the CRM answers
   that too). A failed check is not remembered. */
let cached: { userId: string; access: Promise<boolean>; value?: boolean } | null = null;

function payoutsAccess(userId: string): Promise<boolean> {
  if (cached?.userId !== userId) {
    const entry: { userId: string; access: Promise<boolean>; value?: boolean } = {
      userId,
      access: crmFetch('/v1/payout-hub/accounts', { accountId: '', local: true, method: 'GET' }).then((res) => {
        entry.value = (dataOf<{ account_ids?: unknown[] }>(res).account_ids?.length ?? 0) > 0;
        return entry.value;
      }),
    };
    entry.access.catch(() => {
      if (cached === entry) cached = null;
    });
    cached = entry;
  }
  return cached.access;
}

/** Forget the answer (after access changes). */
export function forgetPayoutsAccess(): void {
  cached = null;
}

/** true / false once known, null while the check is in flight. */
export function usePayoutsAccess(): boolean | null {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id ?? '';
  const known = cached?.userId === userId ? cached.value ?? null : null;
  const [access, setAccess] = useState<boolean | null>(known);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    let live = true;
    payoutsAccess(userId)
      .then((ok) => {
        if (live) setAccess(ok);
      })
      .catch(() => {
        // Hide Payouts rather than open a page with nothing on it.
        if (live) setAccess(false);
      });
    return () => {
      live = false;
    };
  }, [isAuthenticated, userId]);

  return access;
}
